/**
 * StageChecklist (Workspace v2 Slice 3a; group polish #4) — the per-stage
 * intake checklist.
 *
 * Groups items by `maps_to_stage_key` into collapsible <details> sections in
 * pipeline-stage order (the current stage open). WITHIN a stage, items are
 * grouped by their parent (`parent_id`): a parent (an item that has children)
 * renders as a quiet serif/sage GROUP HEADER carrying its own required-progress
 * chip (done/total of its non-optional children) — pepper while incomplete,
 * sage when complete — and its children sit beneath under a light left rail.
 * Done child items recede (muted + strikethrough + smaller) so the eye goes to
 * what's left, and a group auto-collapses to a one-line summary once its
 * required children are all done (expandable on click — the list shortens as
 * they work). Top-level items with no children render as plain rows.
 *
 * NOTE the parent header KEEPS a (quiet) checkbox, but it's a ROLL-UP now, not a
 * peer (#4 cascade): checking a parent cascades to all its children (server-side,
 * toggleChecklistItem) and a parent is done only when its REQUIRED children are.
 * Client Setup's "CareTree – Create / convert client" parent is the advance GATE
 * (is_gate, blocks the move at workItems.ts) — checking it now also satisfies its
 * children, and the gate fires on the rolled-up state.
 *
 * Progress counts REQUIRED LEAF items only (is_optional AND parents excluded — a
 * parent is a roll-up header, not a counted peer), shown in the pepper
 * (terracotta) token while incomplete — a heads-up, not a block. The group chip
 * counts its required children; the stage header counts required leaves. The
 * three tiers (is_gate / neither / is_optional) are functional only; the word
 * "Gate" is never shown (Amanda r1).
 *
 * DESIGN PASS 2026-07-25 (soak r2). The checklist is the drawer's WORKING
 * SURFACE, and it did not look like it: every stage section wore the same
 * dashed rule and the same weight, so a five-stage pipeline read as five
 * equal things and the eye had nowhere to land. Now:
 *   - the CURRENT stage sits on its own quiet tint panel with a forest
 *     left rail ("you are here"), its label at full contrast;
 *   - every other stage compresses to a one-line row in the muted
 *     register — label, count, chevron — inside one divided list, which
 *     is what the completed stages were already nearly doing;
 *   - the section itself is a card on the drawer's shared surface ladder
 *     (drawerLayout), not a bare heading on the beige ground.
 * The #615 advance bar keeps its placement at the end of the current
 * stage's items (law) — it just wears the Button primitive now.
 */

import { Fragment, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/primitives/Button';
import { DrawerSection } from './drawer/drawerLayout';
import { shortDate, stageCountClass, todayStr } from './workspaceFormat';
import { checklistItemRoute } from '@/lib/checklistDeepLinks';
import type { WorkItemChecklistItemRow, PipelineStageWire } from '@/lib/api';
import { DateInput } from '@/components/primitives/DateInput';

const NO_STAGE = '__none';

/** Required (non-optional) done/total for a set of items. Used for a parent's
 *  group chip (its children are all leaves). */
export function requiredProgress(items: WorkItemChecklistItemRow[]): { done: number; total: number } {
  const required = items.filter((i) => !i.is_optional);
  return { done: required.filter((i) => i.is_done).length, total: required.length };
}

/** Required LEAF done/total (#4 cascade): parents (items with a child IN THIS set)
 *  are roll-up headers, not counted peers — so the count "follows the leaves".
 *  Drives the stage header + the card chip; children share their parent's stage,
 *  so a stage-scoped set always contains a parent's children. */
export function requiredLeafProgress(items: WorkItemChecklistItemRow[]): { done: number; total: number } {
  const parentIds = new Set<string>();
  for (const i of items) if (i.parent_id) parentIds.add(i.parent_id);
  const leaves = items.filter((i) => !i.is_optional && !parentIds.has(i.id));
  return { done: leaves.filter((i) => i.is_done).length, total: leaves.length };
}

export interface StageChecklistProps {
  items: WorkItemChecklistItemRow[];
  stages: PipelineStageWire[];
  currentStageKey: string | null;
  canManage: boolean;
  busy: boolean;
  onToggle: (itemId: string, done: boolean) => void;
  onSetNote: (itemId: string, note: string | null) => void;
  /** Template v2 (0124): set an allows_link item's URL (the document-folder
   *  links). Optional so older render sites/tests keep working (input hidden). */
  onSetLink?: (itemId: string, linkUrl: string | null) => void;
  /** Polish fix #1: correct a DONE item's completion date (YYYY-MM-DD). */
  onSetDoneDate?: (itemId: string, doneAt: string) => void;
  /** 0159: fill/clear a typed (date/text) item's value — filled = done,
   *  server-side. Optional so older render sites/tests keep working. */
  onSetValue?: (itemId: string, value: string | null) => void;
  /** Colour fix: the case is past its due_date (item-level). Terracotta counts are
   *  scoped to the CURRENT stage only, so an overdue case doesn't turn every stage red. */
  caseOverdue?: boolean;
  /** Slice 3b: the stage-advance checkpoint ("Complete • Move to next stage") for the
   *  current stage. When null the button is hidden (e.g. a terminal process-board
   *  stage, or a viewer without manage_work_items). */
  onAdvanceCurrentStage?: (() => void) | null;
  /** Soak 2026-07-25: overrides the checkpoint button's label + testid when the
   *  current stage's advance is a different action. Ready for Assignment renders
   *  "Assign care manager" in this slot (opening the Assign dialog) instead of
   *  the generic advance, per the founder's ask that it sit at the end of that
   *  stage's items like every other stage's bar. */
  advanceOverride?: { label: string; testId: string } | null;
  /** Soak r2: a container-supplied lead-in for ONE stage's section, rendered at
   *  the top of that section (before its checklist items) — the intake drawer's
   *  "Open Client Prospect Folder" link on Ready for Assignment. When no items
   *  map to that stage but it IS the current stage, the node rides the fallback
   *  slot (with the checkpoint) so it never vanishes. */
  stageIntro?: { stageKey: string; node: ReactNode } | null;
  /** Rendered directly under the FIRST top-level item of one stage's section
   *  (the drawer's Suggested care managers link under "Care Manager / SPICE
   *  therapist chosen"); under the lead-in when that stage has no items. */
  firstItemAddon?: { stageKey: string; node: ReactNode } | null;
  /** Section heading. The intake drawer keeps the default; process-board
   *  drawers pass "Checklist" (their cards aren't intakes). */
  heading?: string;
  /** Brief 3 D3: tenant slug for a step's deep-link (checklistItemRoute). When
   *  absent (e.g. unit tests render without it) no step renders as a link. */
  tenantSlug?: string;
}

export function StageChecklist({ items, stages, currentStageKey, canManage, busy, onToggle, onSetNote, onSetLink, onSetDoneDate, onSetValue, caseOverdue = false, onAdvanceCurrentStage, advanceOverride, stageIntro = null, firstItemAddon = null, heading = 'Intake checklist', tenantSlug }: StageChecklistProps): ReactNode {
  // Per-group expand override (id → user's explicit open/closed). Default is
  // open-while-incomplete, collapsed-when-complete; a click flips the override.
  const [openOverride, setOpenOverride] = useState<Record<string, boolean>>({});
  // Polish fix #1: per-item "editing completion date" toggle.
  const [editingDate, setEditingDate] = useState<Record<string, boolean>>({});

  // Soak 2026-07-25: an advance action keeps the section alive even with no
  // items — the checkpoint (e.g. the Ready-for-Assignment assign bar) must
  // never disappear just because the checklist is empty. Soak r2: same rule
  // for a current-stage lead-in (the prospect-folder link).
  const hasAdvance = canManage && !!onAdvanceCurrentStage;
  const introIsCurrent = stageIntro !== null && stageIntro.stageKey === currentStageKey;
  const introHasStage = stageIntro !== null && stages.some((s) => s.key === stageIntro.stageKey);
  if (items.length === 0 && !hasAdvance && !introIsCurrent && !introHasStage) return null;

  const childrenByParent = new Map<string, WorkItemChecklistItemRow[]>();
  for (const it of items) {
    if (it.parent_id) (childrenByParent.get(it.parent_id) ?? childrenByParent.set(it.parent_id, []).get(it.parent_id)!).push(it);
  }
  const bySort = (a: WorkItemChecklistItemRow, b: WorkItemChecklistItemRow): number => a.sort_order - b.sort_order;
  const hasChildren = (it: WorkItemChecklistItemRow): boolean => (childrenByParent.get(it.id)?.length ?? 0) > 0;

  const itemsByStage = new Map<string, WorkItemChecklistItemRow[]>();
  for (const it of items) {
    const k = it.maps_to_stage_key ?? NO_STAGE;
    (itemsByStage.get(k) ?? itemsByStage.set(k, []).get(k)!).push(it);
  }

  // Stage sections in pipeline order; a trailing "Other" bucket for null-stage items.
  // A stage carrying the container's lead-in (the Unit picker + folder link
  // on Ready for Assignment) keeps its section even with no items mapped to
  // it, so the lead-in is reachable from any stage of the card.
  const sortedStages = [...stages].sort((a, b) => a.sort_order - b.sort_order);
  const orderedKeys = sortedStages
    .map((s) => s.key)
    .filter((k) => itemsByStage.has(k) || (stageIntro !== null && stageIntro.stageKey === k));
  if (itemsByStage.has(NO_STAGE)) orderedKeys.push(NO_STAGE);
  const labelByKey = new Map(stages.map((s) => [s.key, s.label]));
  // The advance button's terminal-or-not label: the last pipeline stage (highest
  // sort_order) ends in Completed; every earlier stage moves to the next stage.
  // (The terminal transition itself lands with the Assigned-stage cluster.)
  const lastStageKey = sortedStages.length > 0 ? sortedStages[sortedStages.length - 1]!.key : null;
  const isTerminalStage = currentStageKey !== null && currentStageKey === lastStageKey;

  // Slice 3b: the per-stage checkpoint — ticks required non-gate items +
  // advances (label switches on terminal-or-not; • = round bullet), unless the
  // container overrides it (the Ready-for-Assignment assign bar). One node,
  // two render slots: at the end of the CURRENT stage's section when that
  // section exists, else closing the list (so the action survives a stage with
  // no checklist items).
  const advanceButton = hasAdvance ? (
    <Button
      className="mt-2.5 w-full"
      data-testid={advanceOverride?.testId ?? 'stage-advance'}
      disabled={busy}
      onClick={onAdvanceCurrentStage!}
    >
      {advanceOverride?.label ?? (isTerminalStage ? 'Done • Move to Completed' : 'Complete • Move to next stage')}
    </Button>
  ) : null;

  // Template v2 (0124): the link row for an allows_link item — an editable URL
  // input (managers) + an "Open" anchor once a link is saved. The value lives in
  // link_url (PATCH .../checklist/{itemId}, mirroring the note input).
  function renderLinkRow(it: WorkItemChecklistItemRow): ReactNode {
    if (!it.allows_link || !onSetLink) return null;
    return (
      <div className="mt-1 flex items-center gap-2">
        <input
          type="url"
          defaultValue={it.link_url ?? ''}
          disabled={!canManage}
          placeholder="Paste a link"
          aria-label={`${it.label} link`}
          data-testid={`checklist-link-input-${it.id}`}
          onBlur={(e) => { const v = e.target.value.trim(); if (v !== (it.link_url ?? '')) onSetLink(it.id, v || null); }}
          className="w-full rounded-base border border-sage-200 px-2 py-1 text-[12.5px] text-ink bg-paper"
        />
        {it.link_url && (
          <a
            href={it.link_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`checklist-link-open-${it.id}`}
            className="text-[12px] text-sage-800 underline decoration-dotted underline-offset-2 flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded">
            Open
          </a>
        )}
      </div>
    );
  }

  // 0159: the value row for a typed (date/text) item — filling it completes the
  // step server-side (the done triple is written with the value); clearing it
  // un-completes. Mirrors the note/link input pattern (blur-commit).
  function renderValueRow(it: WorkItemChecklistItemRow): ReactNode {
    if ((it.item_type !== 'date' && it.item_type !== 'text') || !onSetValue) return null;
    const commit = (v: string): void => {
      const next = v.trim();
      if (next !== (it.value_text ?? '')) onSetValue(it.id, next || null);
    };
    if (it.item_type === 'date') {
      return (
        <DateInput
          size="compact"
          className="mt-1 w-full max-w-[220px]"
          defaultValue={it.value_text ?? ''}
          disabled={!canManage || busy}
          aria-label={`${it.label} value`}
          data-testid={`checklist-value-${it.id}`}
          onBlur={(e) => commit(e.target.value)}
        />
      );
    }
    return (
      <input
        type="text"
        defaultValue={it.value_text ?? ''}
        disabled={!canManage || busy}
        placeholder="Fill in to complete"
        aria-label={`${it.label} value`}
        data-testid={`checklist-value-${it.id}`}
        onBlur={(e) => commit(e.target.value)}
        className="mt-1 w-full max-w-[220px] rounded-base border border-sage-200 px-2 py-1 text-[12.5px] text-ink bg-paper"
      />
    );
  }

  // A single checkable item row (a leaf root, a child, or deeper nesting). Done
  // items recede so the eye goes to what's left; optional keep the "if
  // applicable" tag (and never count — see requiredProgress).
  function renderItemRow(it: WorkItemChecklistItemRow): ReactNode {
    const grandkids = (childrenByParent.get(it.id) ?? []).sort(bySort);
    // Brief 3 D3: the Assigned-stage steps deep-link to the tool (email drafter /
    // People bio+photo). Opens in a new tab so the checklist stays put. Plain text
    // for every other step (no route). Resolved on the step's rename-safe
    // link_key (0154), so renaming a step in the process editor keeps its link.
    const route = tenantSlug ? checklistItemRoute(it, tenantSlug) : null;
    // 0159: a typed item completes by being FILLED, not ticked — its checkbox
    // is a read-only done indicator (the value input is the interaction), so a
    // tick can never assert done-with-no-value.
    const isTyped = (it.item_type === 'date' || it.item_type === 'text') && !!onSetValue;
    return (
      <div key={it.id}>
        <div className="flex items-start gap-3 py-1">
          <input type="checkbox" className="mt-0.5 w-4 h-4 accent-sage-800 flex-shrink-0" checked={it.is_done} disabled={!canManage || busy || isTyped}
            data-testid={`checklist-item-${it.id}`} aria-label={it.label}
            title={isTyped ? 'Completes when filled in' : undefined}
            onChange={() => onToggle(it.id, !it.is_done)} />
          <div className="min-w-0 flex-1">
            <label
              data-testid={`checklist-label-${it.id}`}
              className={it.is_done ? 'text-[12.5px] text-sage-400 line-through' : 'text-[13.5px] text-ink'}>
              {route ? (
                <Link
                  to={route}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`checklist-link-${it.id}`}
                  className="underline decoration-dotted underline-offset-2 hover:text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded">
                  {it.label}
                </Link>
              ) : it.label}
              {it.is_optional && <span className="ml-1.5 text-[10px] text-sage-400 font-medium normal-case">if applicable</span>}
              {/* Polish fix #1: the completion date is click-to-edit for managers
                  (correct it when entering after the fact); a date picker writes done_at. */}
              {it.is_done && (
                canManage && onSetDoneDate ? (
                  editingDate[it.id] ? (
                    <DateInput
                      autoFocus
                      defaultValue={(it.done_at ?? '').slice(0, 10)}
                      max={todayStr()}
                      aria-label={`${it.label} completion date`}
                      data-testid={`checklist-done-date-input-${it.id}`}
                      onBlur={() => setEditingDate((m) => ({ ...m, [it.id]: false }))}
                      onChange={(e) => {
                        // A completion date records what happened: never a day ahead.
                        if (e.target.value && e.target.value <= todayStr()) {
                          onSetDoneDate(it.id, e.target.value);
                          setEditingDate((m) => ({ ...m, [it.id]: false }));
                        }
                      }}
                      size="compact" className="ml-2"
                    />
                  ) : (
                    <button
                      type="button"
                      data-testid={`checklist-done-at-${it.id}`}
                      onClick={() => setEditingDate((m) => ({ ...m, [it.id]: true }))}
                      className="ml-2 text-[10.5px] text-sage-400 underline decoration-dotted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded"
                      title="Set completion date"
                    >
                      {it.done_at ? shortDate(it.done_at) : 'set date'}
                    </button>
                  )
                ) : (
                  it.done_at && <span className="ml-2 text-[10.5px] text-sage-400" data-testid={`checklist-done-at-${it.id}`}>{shortDate(it.done_at)}</span>
                )
              )}
            </label>
            {renderValueRow(it)}
            {it.allows_note && (
              <textarea
                rows={3}
                defaultValue={it.note ?? ''}
                disabled={!canManage}
                placeholder="Add a note"
                aria-label={`${it.label} note`}
                data-testid={`checklist-note-${it.id}`}
                onBlur={(e) => { const v = e.target.value.trim(); if (v !== (it.note ?? '')) onSetNote(it.id, v || null); }}
                className="mt-1 w-full resize-y rounded-base border border-sage-200 px-2 py-1 text-[12.5px] text-ink bg-paper"
              />
            )}
            {renderLinkRow(it)}
          </div>
        </div>
        {/* Deeper nesting (rare; the Peace template is 2 levels) — indent under a rail. */}
        {grandkids.length > 0 && (
          <div className="ml-2 border-l border-sage-200 pl-3.5">{grandkids.map((g) => renderItemRow(g))}</div>
        )}
      </div>
    );
  }

  // A parent group: quiet serif/sage header (checkbox retained) + children under
  // a left rail. Auto-collapses to the header-only summary when its required
  // children are done; the chevron toggles. The parent checkbox stays reachable
  // collapsed or not (it can be the advance gate).
  function renderGroup(parent: WorkItemChecklistItemRow): ReactNode {
    const kids = (childrenByParent.get(parent.id) ?? []).sort(bySort);
    const { done, total } = requiredProgress(kids);
    const childrenComplete = total > 0 && done === total;
    const open = openOverride[parent.id] ?? !childrenComplete;
    const toggle = (): void => setOpenOverride((m) => ({ ...m, [parent.id]: !open }));

    return (
      <div key={parent.id} className="mt-2.5" data-testid={`checklist-group-${parent.id}`}>
        <div className="flex items-center gap-2.5">
          <input type="checkbox" className="w-4 h-4 accent-sage-800 flex-shrink-0" checked={parent.is_done} disabled={!canManage || busy}
            data-testid={`checklist-item-${parent.id}`} aria-label={parent.label}
            onChange={() => onToggle(parent.id, !parent.is_done)} />
          <button type="button" onClick={toggle} aria-expanded={open}
            data-testid={`checklist-group-toggle-${parent.id}`}
            className="flex items-center gap-2 min-w-0 flex-1 text-left py-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded">
            <span className={'font-display text-[14px] truncate ' + (childrenComplete ? 'text-sage-400' : 'text-sage-800')}>
              {childrenComplete && <span aria-hidden="true">✓ </span>}{parent.label}
            </span>
            <span data-testid={`checklist-group-progress-${parent.id}`}
              className={'ml-auto text-[11.5px] font-semibold flex-shrink-0 ' + stageCountClass(done, total, caseOverdue && parent.maps_to_stage_key === currentStageKey)}>
              {done}/{total}
            </span>
            <span aria-hidden="true" className="text-sage-400 text-[10px] flex-shrink-0">{open ? '▾' : '▸'}</span>
          </button>
        </div>
        {open && (
          <div className="ml-2 mt-0.5 border-l border-sage-200 pl-3.5" data-testid={`checklist-group-body-${parent.id}`}>
            {kids.map((k) => renderItemRow(k))}
            {parent.allows_note && (
              <textarea
                rows={3}
                defaultValue={parent.note ?? ''}
                disabled={!canManage}
                placeholder="Add a note"
                aria-label={`${parent.label} note`}
                data-testid={`checklist-note-${parent.id}`}
                onBlur={(e) => { const v = e.target.value.trim(); if (v !== (parent.note ?? '')) onSetNote(parent.id, v || null); }}
                className="mt-1 mb-1 w-full resize-y rounded-base border border-sage-200 px-2 py-1 text-[12.5px] text-ink bg-paper"
              />
            )}
            {renderValueRow(parent)}
            {renderLinkRow(parent)}
          </div>
        )}
      </div>
    );
  }

  return (
    <DrawerSection title={heading} testId="stage-checklist">
      <div className="divide-y divide-sage-800/[6%]">
        {orderedKeys.map((stageKey) => {
          const stageItems = (itemsByStage.get(stageKey) ?? []);
          const roots = stageItems.filter((i) => i.parent_id === null).sort(bySort);
          const { done, total } = requiredLeafProgress(stageItems);
          // THE working surface: the current stage gets the quiet tint panel
          // and the forest left rail ("you are here"); every other stage
          // compresses to a muted one-line row in the divided list.
          const isCurrent = stageKey === currentStageKey;
          return (
            <details key={stageKey} open={isCurrent} data-testid={`checklist-stage-${stageKey}`}
              className={isCurrent ? 'my-1.5 rounded-base border-l-2 border-l-sage-800 bg-sage-50 px-2.5 py-1.5' : 'py-0.5'}>
              <summary className={'cursor-pointer flex items-center gap-2 list-none py-1 text-[13px] ' + (isCurrent ? 'font-semibold text-sage-800' : 'font-medium text-sage-400')}>
                <span>{labelByKey.get(stageKey) ?? 'Other'}</span>
                <span data-testid={`checklist-stage-progress-${stageKey}`}
                  className={'ml-auto text-[11.5px] font-semibold ' + stageCountClass(done, total, caseOverdue && isCurrent)}>
                  {done}/{total}
                </span>
              </summary>
              <div className="pb-1">
                {/* Soak r2: the container's lead-in (e.g. the prospect-folder
                    link) opens this stage's section, ahead of its items. */}
                {stageIntro?.stageKey === stageKey && stageIntro.node}
                {firstItemAddon?.stageKey === stageKey && roots.length === 0 && firstItemAddon.node}
                {roots.map((r, index) => (
                  <Fragment key={r.id}>
                    {hasChildren(r) ? renderGroup(r) : renderItemRow(r)}
                    {index === 0 && firstItemAddon?.stageKey === stageKey && firstItemAddon.node}
                  </Fragment>
                ))}
                {/* The checkpoint sits at the end of the current stage's items,
                    between this section and the next stage's header. */}
                {isCurrent && advanceButton}
              </div>
            </details>
          );
        })}
      </div>
      {/* Fallback slot: the current stage has no checklist section (no items
          map to it) — the lead-in + checkpoint close the list instead of
          vanishing. */}
      {currentStageKey !== null && !orderedKeys.includes(currentStageKey) && (
        <>
          {introIsCurrent && stageIntro.node}
          {advanceButton}
        </>
      )}
    </DrawerSection>
  );
}
